import { AppSettingType, Co2MaterialType, FilteredElementConfigurationType, ModelCommandType } from "@api/apiClient";
import { useCo2MaterialTypes } from "@api/co2MaterialTypes";
import { useDeleteModelCommand, useModelCommand } from "@api/modelCommand";
import { useFilteredElementConfigurations } from "@api/queryBuilders";
import i18n from "@i18n/i18n";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { Box, Chip, Tooltip } from "@mui/material";
import { gridClasses } from "@mui/x-data-grid";
import { DataGridPremium, GridActionsCellItem, GridColDef, GridDeleteIcon, GridRowParams, GridRowSelectionModel, GridToolbar, useGridApiRef } from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { observer } from "mobx-react";
import { useCallback, useMemo, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

export interface AutoMappingGridProps {
  onDeleteSuccess: () => void;
  onEditClick: (data: any) => void;
}

const AutoMappingGrid = observer((props: AutoMappingGridProps) => {
  const apiRef = useGridApiRef();
  const { onDeleteSuccess, onEditClick } = props;
  const deleteCommand = useDeleteModelCommand();
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const filteredElementConfiguration = useFilteredElementConfigurations(FilteredElementConfigurationType.AutoAssignMaterial);
  const modelCommands = useModelCommand(ModelCommandType.AutoAssignMaterial);
  const co2MaterialTypes = useCo2MaterialTypes();
  const confirm = useConfirm();

  const rows = useMemo(() => {
    if (!filteredElementConfiguration.data?.length || !modelCommands.data?.length || !co2MaterialTypes.data?.length) return [];

    const mappedRows = modelCommands.data.map((modelCommand, index) => {
      try {
        const parsedArgs = modelCommand.arguments ? JSON.parse(modelCommand.arguments) : null;
        if (!parsedArgs) return null;

        const configId = parsedArgs.filteredElementConfigurationId;
        const _filtered = filteredElementConfiguration.data.find((d) => d.filteredElementConfigurationId === configId);

        if (!_filtered) return null;

        let config: any = {};
        let operatorName = "";
        let values: string[] = [];

        if (_filtered.config) {
          try {
            config = JSON.parse(_filtered.config);
            operatorName = Object.keys(config?.data?.and?.[0] ?? {})[0] ?? "";
            values = config?.data?.and?.[0]?.[operatorName]?.[1] ?? [];
          } catch (error) {
            console.error("Error parsing config: ", error);
          }
        }

        return {
          id: index,
          ...modelCommand,
          ..._filtered!,
          operatorName,
          values,
          co2MaterialType: co2MaterialTypes.data.find((c: Co2MaterialType) => c.co2MaterialTypeId === config.co2MaterialTypeId)
        }

      } catch (error) {
        console.error("Error parsing arugunents", error)
        return [];
      }
    });

    return mappedRows.filter((row) => Boolean(row));

  }, [filteredElementConfiguration.data, modelCommands.data, co2MaterialTypes.data]);

  const onEditCommand = useCallback((commandId: string) => {
    const modelCommandDetail: any = rows.find((item: any) => item.commandId === commandId);
    if (modelCommandDetail) {
      onEditClick(modelCommandDetail);
    }
  }, [rows, onEditClick]);


  const onHandleDeleteClick = useCallback(async (commandId: string) => {
    const { confirmed } = await confirm({
      title: i18n.t("auto-mapping.auto-mapping-grid.delete-dialog.header"),
      description: i18n.t("auto-mapping.auto-mapping-grid.delete-dialog.title"),
      confirmationText: i18n.t("dialog.yes"),
      cancellationText: i18n.t("dialog.cancel"),
      dialogProps: {
        maxWidth: "xs",
      },
      confirmationButtonProps: {
        variant: "contained",
        color: "error",
      },
      cancellationButtonProps: {
        variant: "outlined",
      },
    });

    if (confirmed) {
      await deleteCommand.mutateAsync(commandId as any);
      onDeleteSuccess();
    }
  }, [confirm, deleteCommand, onDeleteSuccess]);


  const columns: GridColDef[] = useMemo(() => [
    {
      field: "name",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.cad-material-category"),
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const value = params.value.name || params.value;
        return <>{value ? (value as string).replace(/([a-z])([A-Z])/g, `$1 $2`) : ""}</>;
      },
    },
    {
      field: "values",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.cad-material-value"),
      editable: false,
      flex: 4,

      renderCell: (params) => {
        const values = params.value;

        if (Array.isArray(values)) {
          const filteredValues = values.filter((value) => value !== null);

          const chips = filteredValues.map((value, index) => {
            const label = typeof value === "object" ? value?.label || "" : value?.trim();
            return (
              <Tooltip key={index} title={label}>
                <Chip key={index} label={label} sx={{ "& .MuiChip-label": { display: "block", textWrap: "nowrap" } }} />
              </Tooltip>
            );
          });
          return (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                gap: 0.5,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {chips}
            </Box>
          );
        }
        return null;
      },
    },
    {
      field: "co2MaterialTypeName",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.ec-material"),
      editable: false,
      flex: 2,
      valueGetter: (_value: any, row: any) => row?.co2MaterialType?.name || "",
    },
    {
      field: "a1A2A3",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.ec-factor"),
      editable: false,
      flex: 1,
      type: "number",
      valueGetter: (_value: any, row: any) => row?.co2MaterialType?.a1A2A3 || "",
      valueFormatter: (value: any) => {
        if (value === undefined || value === null) {
          return "";
        }

        return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
      },
    },
    {
      field: "unit",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.declared-unit"),
      editable: false,
      flex: 1,
      valueGetter: (_value: any, row: any) => row?.co2MaterialType?.unit || "",
    },
    {
      field: "profileName",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.profile"),
      editable: false,
      flex: 1,
      renderCell: (params) => {
        const profileName = params.value;
        if (profileName) {
          try {
            return <Box sx={{ wordBreak: "break-word", maxWidth: 200 }}>{profileName}</Box>;
          } catch (error) {
            console.error("Error converting profile to string:", error);
            return "Error";
          }
        }
        return "";
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: i18n.t("auto-mapping.auto-mapping-grid.columns.actions"),
      resizable: false,
      width: 100,
      getActions: (params: GridRowParams) => {
        return [
          <GridActionsCellItem
            key={`reset-${params.id}`}
            icon={
              <Tooltip title={i18n.t("auto-mapping.auto-mapping-grid.columns.edit-button-tooltip")}>
                <ModeEditIcon />
              </Tooltip>
            }
            label={i18n.t("auto-mapping.auto-mapping-grid.columns.edit-button-tooltip")}
            onClick={() => {
              onEditCommand(params.row.commandId as string);
            }}
          />,
          <GridActionsCellItem
            key={`reset-${params.id}`}
            icon={
              <Tooltip title={i18n.t("auto-mapping.auto-mapping-grid.columns.delete-button-tooltip")}>
                <GridDeleteIcon />
              </Tooltip>
            }
            label={i18n.t("auto-mapping.auto-mapping-grid.delete-button-tooltip")}
            onClick={() => {
              onHandleDeleteClick(params.row.commandId as string);
            }}
          />,
        ];
      },
    },
  ], [onEditCommand, onHandleDeleteClick]);

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%", height: `calc(100vh - 420px)` }}>
        <DataGridPremium
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          getRowHeight={() => "auto"}
          loading={modelCommands.isFetching || filteredElementConfiguration.isFetching || co2MaterialTypes.isFetching}
          disableColumnMenu
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 1,
            },
            height: `${(height || 400) - 16}px`,
          }}
          hideFooter={false}
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
            columns: {
              columnVisibilityModel: {
                name: false,
              },
            },
          }}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </>
  );
});

export default AutoMappingGrid;
